import { useState, useEffect } from 'react';
import { useAuth } from '@clerk/clerk-react';
import API_BASE from '../config';

const TYPE_BADGE = {
  proposal: { bg: '#EBF8FF', text: '#2B6CB0', label: 'Proposal' },
  invoice: { bg: '#F0FFF4', text: '#276749', label: 'Invoice' },
  agreement: { bg: '#F5F5F5', text: '#555', label: 'Agreement' },
};

const STATUS_BADGE = {
  sent: { bg: '#FFF8E6', text: '#B7791F' },
  paid: { bg: '#F0FFF4', text: '#276749' },
  cancelled: { bg: '#FFF5F5', text: '#8B1A1A' },
  failed: { bg: '#FFF5F5', text: '#C53030' },
};

function fmtDate(ts) {
  if (!ts) return '—';
  return new Date(ts).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
}

export default function ClientDocuments() {
  const { getToken } = useAuth();
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState({});

  useEffect(() => {
    getToken().then(token =>
      fetch(`${API_BASE}/documents/my`, {
        headers: { 'Authorization': `Bearer ${token}` },
      }).then(r => r.json())
    ).then(data => {
      setDocs(Array.isArray(data) ? data : []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  async function handleDownload(doc) {
    setDownloading(prev => ({ ...prev, [doc.id]: true }));
    try {
      const token = await getToken();
      const res = await fetch(`${API_BASE}/documents/${doc.id}/download`, {
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Download failed');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = doc.file_name || 'document.pdf';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      alert(err.message);
    }
    setDownloading(prev => ({ ...prev, [doc.id]: false }));
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '760px', margin: '0 auto' }}>
      <h1 style={{ fontSize: '1.1rem', fontWeight: '600', color: '#1A1A1A', margin: '0 0 0.25rem' }}>Documents</h1>
      <p style={{ fontSize: '0.875rem', color: '#888', margin: '0 0 2rem' }}>Your proposals, invoices and agreements.</p>

      {loading && <p style={{ fontSize: '0.875rem', color: '#888' }}>Loading…</p>}

      {!loading && docs.length === 0 && (
        <div style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '2rem', textAlign: 'center' }}>
          <p style={{ margin: 0, fontSize: '0.875rem', color: '#888' }}>No documents yet.</p>
        </div>
      )}

      {!loading && docs.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {docs.map(doc => {
            const typeBadge = TYPE_BADGE[doc.type] || TYPE_BADGE.agreement;
            const statusBadge = STATUS_BADGE[doc.status] || STATUS_BADGE.sent;
            return (
              <div key={doc.id} style={{ background: '#fff', border: '0.5px solid #E8E4E0', borderRadius: '10px', padding: '1.25rem 1.5rem', display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.75rem', background: typeBadge.bg, color: typeBadge.text, padding: '0.2rem 0.65rem', borderRadius: '20px', fontWeight: '500', flexShrink: 0 }}>
                  {typeBadge.label}
                </span>

                <div style={{ flex: 1, minWidth: 0 }}>
                  <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '500', color: '#1A1A1A' }}>
                    {doc.service_tier ? doc.service_tier.charAt(0).toUpperCase() + doc.service_tier.slice(1) : 'Document'}
                  </p>
                  <p style={{ margin: '0.1rem 0 0', fontSize: '0.78rem', color: '#888' }}>{fmtDate(doc.sent_at)}</p>
                </div>

                {doc.amount_gbp && (
                  <p style={{ margin: 0, fontSize: '0.88rem', fontWeight: '600', color: '#1A1A1A', flexShrink: 0 }}>
                    £{Number(doc.amount_gbp).toLocaleString('en-GB')}/mo
                  </p>
                )}

                <span style={{ fontSize: '0.75rem', background: statusBadge.bg, color: statusBadge.text, padding: '0.2rem 0.65rem', borderRadius: '20px', fontWeight: '500', textTransform: 'capitalize', flexShrink: 0 }}>
                  {doc.status}
                </span>

                <button
                  onClick={() => handleDownload(doc)}
                  disabled={downloading[doc.id]}
                  style={{ fontSize: '0.78rem', padding: '0.3rem 0.75rem', background: 'transparent', color: '#8B1A1A', border: '1px solid #8B1A1A', borderRadius: '5px', cursor: downloading[doc.id] ? 'default' : 'pointer', opacity: downloading[doc.id] ? 0.6 : 1, flexShrink: 0 }}
                >
                  {downloading[doc.id] ? 'Downloading…' : 'Download PDF'}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
